import { InMemoryWebStorage, UserManager, WebStorageStateStore, type User, type UserManagerSettings } from 'oidc-client-ts';

export type SessionStatus = 'unconfigured' | 'signed-out' | 'signed-in' | 'expired' | 'error' | 'loading';
export interface Session { status: SessionStatus; message?: string; generation: number }

export function oidcSettings(env: Record<string, string | undefined>, origin: string): UserManagerSettings | null {
  const authority = env.VITE_OIDC_ISSUER;
  const clientId = env.VITE_OIDC_CLIENT_ID;
  const redirect = env.VITE_OIDC_REDIRECT_URI;
  const logout = env.VITE_OIDC_POST_LOGOUT_REDIRECT_URI;
  const scope = env.VITE_OIDC_SCOPES;
  if (!authority || !clientId || !redirect || !logout || !scope) return null;
  const issuer = new URL(authority);
  if (issuer.username || issuer.password || issuer.search || issuer.hash ||
    (issuer.protocol !== 'https:' && !(issuer.protocol === 'http:' && ['localhost', '127.0.0.1'].includes(issuer.hostname)))) throw new Error('Invalid OIDC issuer');
  for (const [value, path] of [[redirect, '/auth/callback'], [logout, '/auth/logout']] as const) {
    const url = new URL(value);
    if (url.origin !== origin || url.pathname !== path || url.search || url.hash || url.username || url.password) throw new Error('Invalid OIDC redirect');
  }
  if (!scope.split(/\s+/).includes('openid')) throw new Error('OIDC scopes must include openid');
  return {
    authority, client_id: clientId, redirect_uri: redirect, post_logout_redirect_uri: logout,
    scope, response_type: 'code', response_mode: 'query', disablePKCE: false, staleStateAgeInSeconds: 600,
    automaticSilentRenew: false, loadUserInfo: false, monitorSession: false,
    // Only the short-lived PKCE transaction survives the redirect. Tokens never persist.
    stateStore: new WebStorageStateStore({ store: window.sessionStorage }),
    userStore: new WebStorageStateStore({ store: new InMemoryWebStorage() }),
  };
}

let manager: UserManager | undefined;
let user: User | null = null;
let session: Session = { status: 'unconfigured', generation: 0 };
const listeners = new Set<() => void>();
function update(next: Omit<Session, 'generation'>) { session = { ...next, generation: session.generation + 1 }; listeners.forEach((listener) => listener()); }
export const getSession = () => session;
// Each immutable snapshot is a session generation, even for the same account.
export function assertCurrentSession(expected: Session) {
  if (expected !== session || session.status !== 'signed-in') throw new Error('The originating session ended. Sign in and reload to confirm server state.');
}
export const subscribeSession = (listener: () => void) => { listeners.add(listener); return () => { listeners.delete(listener); }; };

export function expireSession() {
  user = null;
  void manager?.removeUser();
  update({ status: 'expired', message: 'Your session expired. Sign in again to continue.' });
}

export function accessToken(): string | undefined {
  if (!user) return undefined;
  if (!user.access_token || user.token_type.toLowerCase() !== 'bearer' || !user.expires_at || user.expires_at <= Date.now() / 1000) {
    expireSession();
    return undefined;
  }
  return user.access_token;
}

let initialization: Promise<void> | undefined;
export function initializeSession(): Promise<void> {
  return initialization ??= initialize();
}
async function initialize() {
  const callback = window.location.pathname === '/auth/callback';
  const logoutCallback = window.location.pathname === '/auth/logout';
  try {
    const settings = oidcSettings(import.meta.env, window.location.origin);
    if (!settings) { update({ status: 'unconfigured' }); return; }
    manager = new UserManager(settings);
    manager.events.addUserLoaded((loaded) => { user = loaded; if (accessToken()) update({ status: 'signed-in' }); });
    manager.events.addAccessTokenExpired(expireSession);
    manager.events.addUserUnloaded(() => { user = null; if (session.status === 'signed-in') update({ status: 'signed-out' }); });
    update({ status: 'loading' });
    if (callback) {
      user = await manager.signinRedirectCallback(window.location.href);
      if (accessToken()) update({ status: 'signed-in' });
    } else {
      if (logoutCallback) await manager.signoutRedirectCallback(window.location.href);
      update({ status: 'signed-out' });
    }
    await manager.clearStaleState();
  } catch {
    user = null;
    await manager?.removeUser();
    update({ status: 'error', message: 'Sign-in could not be completed. Check the identity-provider configuration and try again.' });
  } finally {
    // Never use an untrusted return URL, or leave authorization codes in browser history.
    if (callback || logoutCallback) window.history.replaceState(null, '', '/agents');
  }
}

export async function signIn() {
  if (!manager) return;
  update({ status: 'loading' });
  try { await manager.signinRedirect(); }
  catch { update({ status: 'error', message: 'Sign-in could not be started. Try again or contact your administrator.' }); }
}

export async function signOut() {
  if (!manager) return;
  // Clear local access before contacting the provider, even if provider logout fails.
  user = null;
  update({ status: 'signed-out' });
  try {
    await manager.removeUser();
    await manager.signoutRedirect({ extraQueryParams: { client_id: manager.settings.client_id } });
  } catch {
    update({ status: 'error', message: 'Signed out of this console. Provider logout failed; close your provider session separately.' });
  }
}
